import { getFontFamilyStyle } from '../font';
import { CompositeSegment } from '../../MainPlayer/types/Segment/CompositeSegment';
import { SegmentType } from '../../MainPlayer/types/Segment/Type';
import { SegmentsTrack } from '../../MainPlayer/types/Track/SegmentsTrack';
import { CaptionSegmentData, CaptionTrackMetadata } from '../../MainPlayer/types/Track/CaptionsTrack/CaptionsTrack';
import { createOneWordPages } from './page-creators/one-word-pages';
import { updatePagesWithCallOutElements } from './page-creators/call-out-pages';
import { createDefaultPages } from './page-creators/default-pages';
import { getStylesForPage } from './styles';
import { Page } from '../../types';
import { ElementsTrack } from '../../MainPlayer/types/Track/ElementsTrack';
import { getPagesForLineConfig } from './generate-caption-pages';
import { CaptionLineConfig, Clip, ClipMetadata } from '@/domains/asset';
import { getClipFPS, isClipInOriginalLanguage } from '@/libs/clipUtils';
import { fullTranscriptStore } from '@/stores/fullTranscriptStore';
import { TranscriptProcessor } from '@/libs/transcriptUtils';
import { Word } from '@/domains/transcript';
import { getCaptionsForSegment } from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/playerConfigUtil';
import { getAllSegments } from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/clipUpdateDataUtilsWithSplits/compositeUtils';
import { isSplitsInTimelineEnabled } from '@/stores/clip';
import { FONT_DEFAULT_VALUE } from '@/Pages/Clip/SideBar/FontSelector/constants';

export function getCaptionPages({
  clip,
  // Needs updated main track to calculate the frame offset for splits
  mainTrack,
  updatedElementTracks,
  captionTrackOffset
}: {
  clip: Clip;
  mainTrack?: SegmentsTrack;
  updatedElementTracks?: ElementsTrack[];
  captionTrackOffset: number;
}): Page[] {
  const allPages: Page[] = [];
  const isSplitsEnabled = isSplitsInTimelineEnabled(clip);
  const allSegments = mainTrack ? mainTrack.segments : getAllSegments(clip);
  let frameOffset = 0;
  for (const segment of allSegments) {
    if (segment.type === SegmentType.COMPOSITE) {
      const captions = getCaptionsForSegment(clip, segment.id) || [];
      if (captions.length) {
        const fps = getClipFPS(clip);
        const fontFamily = getFontFamilyStyle(
          clip.id,
          clip.asset_metadata.caption_styles?.font_location || clip.asset_metadata.font_location
        );
        const captionStyles = clip.asset_metadata.caption_styles;
        const size = clip.asset_metadata.size;
        const layout = clip.asset_metadata.layout;
        const captionPosition = clip.asset_metadata.caption_positions;
        const stylesForPage = getStylesForPage({
          captionPosition,
          captionStyles,
          size,
          layout,
          fontFamily: fontFamily || FONT_DEFAULT_VALUE.name
        });
        const transitions = clip.asset_metadata.caption_styles?.transitions;

        const defaultLineConfig = CaptionLineConfig.OneLine;

        const lineConfig = clip.asset_metadata.caption_styles?.line_config || defaultLineConfig;

        let pages: Page[] = [];

        if (lineConfig === CaptionLineConfig.OneWord) {
          pages = createOneWordPages({ captions, fps, stylesForPage, transitions });
        } else if (
          [CaptionLineConfig.OneLine, CaptionLineConfig.TwoLines, CaptionLineConfig.ThreeLines].includes(lineConfig)
        ) {
          pages = getPagesForLineConfig({
            captions,
            fontFamily,
            captionStyles,
            stylesForPage,
            fps,
            transitions,
            lineConfig,
            size
          });
        } else {
          const clipDurationFrames = Math.round((clip.asset_metadata.end - clip.asset_metadata.start) * fps);
          pages = createDefaultPages({ captions, fps, stylesForPage, layout, clipDurationFrames, transitions });
        }

        const currentFrameOffset = frameOffset;
        if (isSplitsEnabled) {
          pages = pages.map(page => {
            return {
              ...page,
              fromFrame: page.fromFrame + currentFrameOffset,
              toFrame: page.toFrame + currentFrameOffset,
              tokens: page.tokens.map(token => ({
                ...token,
                fromFrame: token.fromFrame + currentFrameOffset,
                toFrame: token.toFrame + currentFrameOffset
              }))
            };
          });
        }
        // Process call-out elements for all normal pages
        allPages.push(...updatePagesWithCallOutElements(pages, clip, updatedElementTracks, captionTrackOffset));
      }
    }
    frameOffset += segment.toFrame - segment.fromFrame;
  }
  return allPages;
}

export function buildCaptionMetadata(clip: Clip, mainTrack?: SegmentsTrack): CaptionTrackMetadata {
  const allSegments = mainTrack ? mainTrack.segments : getAllSegments(clip);
  const segmentWiseData: CaptionSegmentData[] = [];
  let frameOffset = 0;

  const isSplitsEnabled = isSplitsInTimelineEnabled(clip);

  for (const segment of allSegments) {
    if (segment.type === SegmentType.COMPOSITE) {
      const captions = getCaptionsForSegment(clip, segment.id) || [];
      segmentWiseData.push({
        segmentId: segment.id,
        segmentType: SegmentType.COMPOSITE,
        captions,
        offset: isSplitsEnabled ? frameOffset : 0
      });
    }
    frameOffset += segment.toFrame - segment.fromFrame;
  }

  const fontFamily = getFontFamilyStyle(
    clip.id,
    clip.asset_metadata.caption_styles?.font_location || clip.asset_metadata.font_location
  );

  return {
    segmentWiseData,
    fontFamily,
    captionStyles: clip.asset_metadata.caption_styles,
    lineConfig: clip.asset_metadata.caption_styles?.line_config || CaptionLineConfig.OneLine,
    captionPosition: clip.asset_metadata.caption_positions,
    size: clip.asset_metadata.size,
    layout: clip.asset_metadata.layout,
    clipStart: clip.asset_metadata.start,
    clipEnd: clip.asset_metadata.end
  };
}

const calculateExcludesAndDurationWithData = ({
  start,
  end,
  edits,
  deletes,
  merges
}: Pick<ClipMetadata, 'start' | 'end' | 'edits' | 'deletes' | 'merges'>) => {
  const originalFullTranscript = fullTranscriptStore.getSnapshot();
  // Excludes and duration should ignore translated captions
  const transcriptProcessorWithoutTranslatedCaptions = new TranscriptProcessor(start, end, originalFullTranscript);
  transcriptProcessorWithoutTranslatedCaptions.getFinalCaptions(edits ?? {}, deletes ?? {}, merges ?? []);
  const excludes = transcriptProcessorWithoutTranslatedCaptions.getExcludes(
    (deletes ?? {}) as unknown as Parameters<typeof transcriptProcessorWithoutTranslatedCaptions.getFinalCaptions>[1]
  );
  const duration = transcriptProcessorWithoutTranslatedCaptions.getDuration(deletes ?? {});
  return { excludes, duration };
};

export const computeCaptionsWithData = ({
  start,
  end,
  edits,
  deletes,
  merges,
  isTranslated,
  translatedCaptionsSource = []
}: Pick<ClipMetadata, 'start' | 'end' | 'edits' | 'deletes' | 'merges'> & {
  isTranslated: boolean;
  translatedCaptionsSource?: Word[];
}) => {
  const originalFullTranscript = fullTranscriptStore.getSnapshot();
  let translatedCaptions: Word[] | undefined = undefined;
  if (isTranslated) {
    translatedCaptions = translatedCaptionsSource ?? [];
  }

  const transcriptProcessor = new TranscriptProcessor(start, end, originalFullTranscript, translatedCaptions);
  const captions = transcriptProcessor.getFinalCaptions(edits ?? {}, deletes ?? {}, merges ?? []);

  const { excludes, duration } = calculateExcludesAndDurationWithData({ start, end, edits, merges, deletes });

  return {
    captions,
    excludes,
    duration
  };
};

export const computeCaptionsForClip = (clip: Clip) => {
  const { start, end, edits, deletes, merges, captions } = clip.asset_metadata;
  return computeCaptionsWithData({
    start,
    end,
    edits,
    deletes,
    merges,
    isTranslated: !isClipInOriginalLanguage(clip),
    translatedCaptionsSource: captions
  });
};

export const computeCaptionsForComposite = (compositeSegment: CompositeSegment, clip: Clip) => {
  if (!compositeSegment.segment_metadata) {
    return;
  }
  const { start, end, edits, deletes, merges, captions } = compositeSegment.segment_metadata;

  return computeCaptionsWithData({
    start,
    end,
    edits,
    deletes,
    merges,
    isTranslated: !isClipInOriginalLanguage(clip),
    translatedCaptionsSource: captions
  });
};
